// ============================================
// CHÁMOMILE - Animation Utilities
// High-impact animations for premium UX
// ============================================

// Stagger children animation for grid items
export const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: {
            staggerChildren: 0.08,
            delayChildren: 0.1,
        },
    },
};

// Individual card entrance animation
export const cardVariants = {
    hidden: {
        opacity: 0,
        y: 30,
        scale: 0.95,
    },
    visible: {
        opacity: 1,
        y: 0,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 300,
            damping: 24,
        },
    },
};

// Fade in from bottom
export const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.4,
            ease: [0.4, 0, 0.2, 1],
        },
    },
};

// Scale pop animation
export const popIn = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: {
            type: "spring",
            stiffness: 400,
            damping: 20,
        },
    },
};

// Page transition variants
export const pageVariants = {
    initial: { opacity: 0, x: 20 },
    enter: {
        opacity: 1,
        x: 0,
        transition: {
            duration: 0.3,
            ease: [0.4, 0, 0.2, 1],
        },
    },
    exit: {
        opacity: 0,
        x: -20,
        transition: {
            duration: 0.2,
            ease: [0.4, 0, 0.2, 1],
        },
    },
};

// Success celebration animation
export const successVariants = {
    hidden: { scale: 0, rotate: -180 },
    visible: {
        scale: 1,
        rotate: 0,
        transition: {
            type: "spring",
            stiffness: 200,
            damping: 15,
        },
    },
};

// Button hover/tap states
export const buttonVariants = {
    hover: {
        scale: 1.05,
        transition: { duration: 0.2 },
    },
    tap: {
        scale: 0.95,
        transition: { duration: 0.1 },
    },
};

// Flying item animation for add-to-cart
export const createFlyingAnimation = (startRect, endRect) => {
    const deltaX = endRect.left - startRect.left + (endRect.width - startRect.width) / 2;
    const deltaY = endRect.top - startRect.top + (endRect.height - startRect.height) / 2;

    return {
        initial: {
            x: 0,
            y: 0,
            scale: 1,
            opacity: 1,
        },
        animate: {
            x: deltaX,
            y: deltaY,
            scale: 0.2,
            opacity: 0,
            transition: {
                duration: 0.6,
                ease: [0.4, 0, 0.2, 1],
            },
        },
    };
};

// Confetti configuration
export const confettiConfig = {
    colors: ['#c9a962', '#b8943d', '#dccaa6', '#f5f0e6', '#a07d2e'],
    particleCount: 50,
    spread: 70,
    origin: { y: 0.6 },
    gravity: 1.2,
    ticks: 150,
};

// Create confetti particles (pure CSS/JS implementation)
export const createConfetti = (originX = 0.5, originY = 0.5) => {
    const colors = confettiConfig.colors;
    const container = document.createElement('div');
    container.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 9999;
    overflow: hidden;
  `;
    document.body.appendChild(container);

    for (let i = 0; i < confettiConfig.particleCount; i++) {
        const particle = document.createElement('div');
        const color = colors[Math.floor(Math.random() * colors.length)];
        const size = Math.random() * 8 + 4;
        const startX = originX * window.innerWidth;
        const startY = originY * window.innerHeight;
        const angle = Math.random() * Math.PI * 2;
        const velocity = Math.random() * 400 + 200;
        const rotationSpeed = (Math.random() - 0.5) * 720;

        particle.style.cssText = `
      position: absolute;
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
      left: ${startX}px;
      top: ${startY}px;
      transform-origin: center;
    `;

        container.appendChild(particle);

        // Animate particle
        const animation = particle.animate([
            {
                transform: 'translate(0, 0) rotate(0deg)',
                opacity: 1,
            },
            {
                transform: `translate(${Math.cos(angle) * velocity}px, ${Math.sin(angle) * velocity + 300}px) rotate(${rotationSpeed}deg)`,
                opacity: 0,
            },
        ], {
            duration: 1500 + Math.random() * 500,
            easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)',
        });

        animation.onfinish = () => particle.remove();
    }

    // Cleanup container after all particles are done
    setTimeout(() => container.remove(), 2500);
};

// Ripple effect for buttons
export const createRipple = (event, color = 'rgba(201, 169, 98, 0.4)') => {
    const button = event.currentTarget;
    const rect = button.getBoundingClientRect();
    const ripple = document.createElement('span');
    const diameter = Math.max(rect.width, rect.height);
    const radius = diameter / 2;

    ripple.style.cssText = `
    position: absolute;
    width: ${diameter}px;
    height: ${diameter}px;
    left: ${event.clientX - rect.left - radius}px;
    top: ${event.clientY - rect.top - radius}px;
    background: ${color};
    border-radius: 50%;
    transform: scale(0);
    animation: ripple-effect 0.6s ease-out;
    pointer-events: none;
  `;

    // Ensure button has relative positioning
    const computedStyle = window.getComputedStyle(button);
    if (computedStyle.position === 'static') {
        button.style.position = 'relative';
    }
    button.style.overflow = 'hidden';

    button.appendChild(ripple);

    ripple.addEventListener('animationend', () => ripple.remove());
};

// Accessibility: Check if user prefers reduced motion
export const prefersReducedMotion = () => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Wrapper for motion-safe animations
export const getMotionVariants = (variants) => {
    if (prefersReducedMotion()) {
        return {
            hidden: { opacity: 0 },
            visible: { opacity: 1, transition: { duration: 0.2 } },
        };
    }
    return variants;
};
